/**
 * PRODUCTS-WORKSPACE-000001 — product status helpers.
 * Classifies only from declared CatalogProduct fields. Never invents stock/KPI totals.
 */
import type { CatalogProduct } from '../../../api/amazonconnect';
import { isFullCatalogKnown } from '../../../commerce-workspace';

export function productStatusText(s: unknown): string {
  if (s == null) return '';
  if (Array.isArray(s)) return s.join(', ');
  if (typeof s === 'string') return s;
  return JSON.stringify(s);
}

export function productInventoryQty(p: CatalogProduct): number | null {
  const raw = p.inventory_reference ?? p.inventory;
  if (raw == null || raw === '') return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * Founder attention bucket from existing listing fields only.
 * - active: BUYABLE / DISCOVERABLE-like status
 * - suppressed: NOT_DISCOVERABLE / DELETED / suppress-like
 * - oos: inventory qty known and 0
 * - attention: missing ASIN, title, or status
 * - other: known but unclassified
 */
export type ProductBucket = 'active' | 'suppressed' | 'oos' | 'attention' | 'other';

export function classifyProduct(p: CatalogProduct): ProductBucket {
  const st = productStatusText(p.status).toUpperCase();
  const inv = productInventoryQty(p);
  const needsAttention = !p.asin || !p.title || !st;

  if (needsAttention) return 'attention';
  if (st.includes('DELETE') || st.includes('NOT_DISCOVERABLE') || st.includes('SUPPRESS')) {
    return 'suppressed';
  }
  if (inv === 0) return 'oos';
  if (st.includes('BUYABLE') || st.includes('DISCOVERABLE') || st.includes('ACTIVE')) {
    return 'active';
  }
  return 'other';
}

export type ProductSummaryCounts = {
  /** Full-catalog database count when API provides total_count */
  totalDb: number | null;
  amazonCount: number | null;
  displayed: number;
  /**
   * Full-catalog breakdown is only trustworthy when every matching row is on this page
   * (returned === total and no residual page).
   */
  fullCatalogKnown: boolean;
  active: number | null;
  suppressed: number | null;
  oos: number | null;
  attention: number | null;
  /** Page-slice counts always available from current rows */
  pageActive: number;
  pageSuppressed: number;
  pageOos: number;
  pageAttention: number;
};

export function summarizeProducts(
  products: CatalogProduct[],
  opts: {
    totalCount?: number | null;
    returnedCount?: number | null;
    amazonCount?: number | null;
    hasFilter?: boolean;
  },
): ProductSummaryCounts {
  let pageActive = 0;
  let pageSuppressed = 0;
  let pageOos = 0;
  let pageAttention = 0;
  for (const p of products) {
    const b = classifyProduct(p);
    if (b === 'active') pageActive += 1;
    else if (b === 'suppressed') pageSuppressed += 1;
    else if (b === 'oos') pageOos += 1;
    else if (b === 'attention') pageAttention += 1;
  }

  const totalDb = opts.totalCount ?? null;
  const returned = opts.returnedCount ?? products.length;
  const fullCatalogKnown = isFullCatalogKnown({
    totalCount: totalDb,
    returnedCount: returned,
    rowCount: products.length,
    hasFilter: opts.hasFilter,
  });

  return {
    totalDb,
    amazonCount: opts.amazonCount ?? null,
    displayed: returned,
    fullCatalogKnown,
    active: fullCatalogKnown ? pageActive : null,
    suppressed: fullCatalogKnown ? pageSuppressed : null,
    oos: fullCatalogKnown ? pageOos : null,
    attention: fullCatalogKnown ? pageAttention : null,
    pageActive,
    pageSuppressed,
    pageOos,
    pageAttention,
  };
}
